import { useEffect, useMemo, useState } from "react";
import {
  Target, Compass, TrendingUp, ShieldCheck, AlertTriangle, ChevronDown,
  Sparkles, ArrowRight, Star, Gauge, Zap, ChevronRight, CalendarRange,
} from "lucide-react";
import client from "../../lib/api";

const HUES = {
  blue: "219 76% 47%",
  teal: "178 82% 34%",
  violet: "258 60% 55%",
  amber: "32 80% 45%",
  green: "165 70% 34%",
  pink: "336 60% 52%",
};
const AXIS_HUES = [HUES.teal, HUES.blue, HUES.violet, HUES.pink, HUES.amber, HUES.green];

// A small progress ring used for fit, readiness, and the goal gauge.
export function Ring({ pct = 0, color = HUES.blue, size = 68, stroke = 7, showOf = true }) {
  const r = (size - stroke * 2) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--klareet-line))" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`hsl(${color})`} strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 800ms cubic-bezier(0.22,1,0.36,1)" }}
      />
      <text x="50%" y="47%" textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize: size * 0.3, fontWeight: 700, fill: "hsl(var(--klareet-ink))" }}>{Math.round(pct)}</text>
      {showOf && (
        <text x="50%" y="66%" textAnchor="middle" style={{ fontSize: 8.5, fill: "hsl(var(--klareet-muted))" }}>of 100</text>
      )}
    </svg>
  );
}

// ---- Interest compass (RIASEC hexagon) --------------------------------
export function InterestCompass({ data = [] }) {
  const size = 300, cx = size / 2, cy = size / 2, R = 108;
  const n = data.length || 6;
  const pt = (i, radius) => {
    const a = (-90 + (360 / n) * i) * (Math.PI / 180);
    return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)];
  };
  const grid = [0.34, 0.67, 1].map((f) =>
    data.map((_, i) => pt(i, R * f).join(",")).join(" ")
  );
  const poly = data.map((d, i) => pt(i, R * (d.pct / 100)).join(",")).join(" ");
  return (
    <div className="k-card" data-testid="insight-compass">
      <div className="k-label mb-1 flex items-center gap-2"><Compass size={14} /> interest compass</div>
      <p className="k-quiet !text-xs mb-3">Where your pull concentrates across the six work interests.</p>
      <div className="flex flex-col items-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {grid.map((g, i) => (
            <polygon key={i} points={g} fill="none" stroke="hsl(var(--klareet-line))" strokeWidth="1" />
          ))}
          {data.map((_, i) => {
            const [x, y] = pt(i, R);
            return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="hsl(var(--klareet-line))" strokeWidth="1" />;
          })}
          <polygon points={poly} fill="hsl(219 76% 47% / 0.16)" stroke="hsl(219 76% 47%)" strokeWidth="2" />
          {data.map((d, i) => {
            const [x, y] = pt(i, R * (d.pct / 100));
            return <circle key={i} cx={x} cy={y} r="3.5" fill={`hsl(${AXIS_HUES[i % AXIS_HUES.length]})`} />;
          })}
          {data.map((d, i) => {
            const [x, y] = pt(i, R + 20);
            return (
              <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                style={{ fontSize: 11, fontWeight: 600, fill: "hsl(var(--klareet-ink-soft))" }}>{d.label}</text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

// ---- Generic bar list (behaviour, aptitudes, drives) ------------------
export function BarList({ title, sub, icon, data = [], hue = HUES.violet, testid }) {
  return (
    <div className="k-card" data-testid={testid}>
      <div className="k-label mb-1 flex items-center gap-2">{icon} {title}</div>
      {sub && <p className="k-quiet !text-xs mb-3">{sub}</p>}
      <div className="space-y-3 mt-2">
        {data.map((d) => (
          <div key={d.key || d.label} data-testid={`bar-${(d.key || d.label)}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>{d.label}</span>
              <span className="klareet-mono !text-xs" style={{ color: "hsl(var(--klareet-muted))" }}>{d.pct}</span>
            </div>
            <div className="k-progress-track" style={{ height: 8 }}>
              <div style={{ width: `${d.pct}%`, height: "100%", borderRadius: 999, background: `hsl(${hue})`, transition: "width 700ms ease" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Goal readiness gauge ---------------------------------------------
export function GoalReadinessGauge({ data }) {
  if (!data) return null;
  return (
    <div className="k-accent-card k-acc-teal" data-testid="goal-readiness-gauge">
      <div className="k-acc-label mb-3 flex items-center gap-2"><Gauge size={14} /> goal readiness</div>
      <div className="flex items-center gap-5 flex-wrap">
        <Ring pct={data.pct} color={HUES.teal} size={92} stroke={9} />
        <div className="flex-1 min-w-[220px] space-y-2.5">
          {data.sub.map((s) => (
            <div key={s.label} data-testid={`readiness-sub-${s.label}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>{s.label}</span>
                <span className="klareet-mono !text-xs" style={{ color: "hsl(var(--klareet-muted))" }}>{s.pct}</span>
              </div>
              <div className="k-progress-track" style={{ height: 7 }}>
                <div style={{ width: `${s.pct}%`, height: "100%", borderRadius: 999, background: "hsl(178 82% 34%)" }} />
              </div>
              <div className="k-quiet !text-[11px] mt-0.5">{s.hint}</div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-sm mt-4" style={{ color: "hsl(var(--klareet-ink))" }} data-testid="goal-readiness-note">{data.note}</p>
    </div>
  );
}

// ---- Strengths and watch-outs -----------------------------------------
export function StrengthsWatchouts({ data }) {
  if (!data) return null;
  return (
    <div className="k-card" data-testid="strengths-watchouts">
      <div className="k-label mb-3 flex items-center gap-2"><Zap size={14} /> strengths and watch-outs</div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <div className="k-acc-label mb-2" style={{ color: "hsl(178 82% 30%)" }}>what you lead with</div>
          <ul className="space-y-2">
            {data.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`strength-${i}`}>
                <ShieldCheck size={15} className="shrink-0 mt-0.5" style={{ color: "hsl(178 82% 34%)" }} /> {s.line}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="k-acc-label mb-2" style={{ color: "hsl(32 80% 38%)" }}>what to manage</div>
          <ul className="space-y-2">
            {data.watchouts.length ? data.watchouts.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`watchout-${i}`}>
                <AlertTriangle size={15} className="shrink-0 mt-0.5" style={{ color: "hsl(32 80% 45%)" }} /> {s.line}
              </li>
            )) : <li className="k-quiet !text-sm">No sharp weak spots stand out yet.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---- Behavioural study (per-dimension deep read) ----------------------
export function BehaviouralStudy({ dimensions = [] }) {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="behavioural-study">
      <div className="k-label mb-3 flex items-center gap-2"><Sparkles size={14} /> your behavioural study</div>
      <div className="space-y-3">
        {dimensions.map((d, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="k-card !p-0 overflow-hidden" data-testid={`dimension-${i}`}>
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full flex items-center gap-3 p-4 text-left"
                data-testid={`dimension-${i}-toggle`}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "hsl(219 76% 47% / 0.1)" }}>
                  <span className="klareet-mono text-sm font-bold" style={{ color: "hsl(219 76% 47%)" }}>{d.pct}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>{d.label}</div>
                  <div className="k-quiet !text-xs truncate">{d.meaning}</div>
                </div>
                <ChevronDown size={18} style={{ color: "hsl(var(--klareet-muted))", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 space-y-3 k-fade-in" data-testid={`dimension-${i}-body`}>
                  <Row label="Your read" text={d.your_read} />
                  <Row label="Strength" text={d.strengths} />
                  <Row label="Watch-out" text={d.watchouts} />
                  <Row label="Do this" text={d.do_this} accent />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ label, text, accent }) {
  return (
    <div className="flex gap-3">
      <div className="k-acc-label shrink-0 w-24" style={{ color: accent ? "hsl(178 82% 30%)" : undefined }}>{label}</div>
      <div className="text-sm flex-1" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.55 }}>{text}</div>
    </div>
  );
}

// ---- Fit x Readiness map (quadrant scatter) ---------------------------
export function FitReadinessMap({ roles = [], selected, onSelect, master }) {
  const size = 320, pad = 34, plot = size - pad * 2;
  const x = (fit) => pad + (fit / 100) * plot;
  const y = (rd) => pad + (1 - rd / 100) * plot;
  const mid = pad + plot / 2;
  return (
    <div className="k-card" data-testid="fit-readiness-map">
      <div className="k-label mb-1 flex items-center gap-2"><Target size={14} /> fit and readiness map</div>
      <p className="k-quiet !text-xs mb-2">Every path placed by how well it fits you and how ready you are.</p>
      <svg width="100%" viewBox={`0 0 ${size} ${size}`} style={{ maxWidth: 360 }}>
        <rect x={mid} y={pad} width={plot / 2} height={plot / 2} fill="hsl(178 82% 34% / 0.07)" />
        <line x1={mid} y1={pad} x2={mid} y2={size - pad} stroke="hsl(var(--klareet-line))" strokeDasharray="4 4" />
        <line x1={pad} y1={mid} x2={size - pad} y2={mid} stroke="hsl(var(--klareet-line))" strokeDasharray="4 4" />
        <text x={size - pad} y={pad - 10} textAnchor="end" style={{ fontSize: 9.5, fill: "hsl(178 82% 30%)", fontWeight: 600 }}>sweet spot</text>
        <text x={pad} y={pad - 10} textAnchor="start" style={{ fontSize: 9.5, fill: "hsl(var(--klareet-muted))" }}>backup</text>
        <text x={size - pad} y={size - pad + 16} textAnchor="end" style={{ fontSize: 9.5, fill: "hsl(var(--klareet-muted))" }}>stretch</text>
        <text x={pad} y={size - pad + 16} textAnchor="start" style={{ fontSize: 9.5, fill: "hsl(var(--klareet-muted))" }}>not yet</text>
        <text x={mid} y={size - 6} textAnchor="middle" style={{ fontSize: 10, fill: "hsl(var(--klareet-muted))" }}>fit →</text>
        <text x={11} y={mid} textAnchor="middle" transform={`rotate(-90 11 ${mid})`} style={{ fontSize: 10, fill: "hsl(var(--klareet-muted))" }}>readiness →</text>
        {roles.map((r) => {
          const isSel = r.name === selected;
          const isMaster = r.name === master;
          return (
            <g key={r.name} onClick={() => onSelect && onSelect(r.name)} style={{ cursor: "pointer" }} data-testid={`map-dot-${r.name}`}>
              <circle cx={x(r.fit)} cy={y(r.readiness)} r={isSel ? 8 : 6}
                fill={isMaster ? "hsl(258 60% 55%)" : "hsl(219 76% 47%)"}
                stroke={isSel ? "hsl(var(--klareet-ink))" : "white"} strokeWidth={isSel ? 2.5 : 1.5}>
                <title>{`${r.name}: fit ${r.fit}, readiness ${r.readiness}`}</title>
              </circle>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ---- Role fit ranking -------------------------------------------------
export function RoleFitRanking({ roles = [], selected, onSelect }) {
  const sorted = [...roles].sort((a, b) => b.fit - a.fit);
  return (
    <div className="k-card" data-testid="role-fit-ranking">
      <div className="k-label mb-3 flex items-center gap-2"><TrendingUp size={14} /> how the paths rank for you</div>
      <div className="space-y-2">
        {sorted.map((r) => {
          const isSel = r.name === selected;
          return (
            <button key={r.name} onClick={() => onSelect && onSelect(r.name)}
              className="w-full flex items-center gap-3 text-left group" data-testid={`rank-${r.name}`}>
              <span className="text-sm w-32 shrink-0 truncate" style={{ color: isSel ? "hsl(219 76% 47%)" : "hsl(var(--klareet-ink))", fontWeight: isSel ? 600 : 400 }}>{r.name}</span>
              <span className="flex-1 k-progress-track" style={{ height: 9 }}>
                <span style={{ display: "block", width: `${r.fit}%`, height: "100%", borderRadius: 999, background: isSel ? "hsl(258 60% 55%)" : "hsl(219 76% 47%)", transition: "width 600ms ease" }} />
              </span>
              <span className="klareet-mono !text-xs w-7 text-right" style={{ color: "hsl(var(--klareet-muted))" }}>{r.fit}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---- Career fit panel (master rec + interactive roles) ----------------
export function CareerFit({ insight, goalTitle, product = "start" }) {
  const cf = insight?.career_fit;
  const roles = useMemo(() => cf?.roles || [], [cf?.roles]);
  const [selected, setSelected] = useState(cf?.master || (roles[0] && roles[0].name));
  const [plans, setPlans] = useState({});
  const [planBusy, setPlanBusy] = useState("");
  useEffect(() => { setSelected(cf?.master || (roles[0] && roles[0].name)); }, [cf?.master]); // eslint-disable-line
  const sel = useMemo(() => roles.find((r) => r.name === selected) || roles[0], [roles, selected]);
  const masterRole = roles.find((r) => r.name === cf?.master) || roles[0];

  const loadPlan = async (name) => {
    if (!name || plans[name] || planBusy) return;
    setPlanBusy(name);
    try {
      const r = await client.get(`/demo/role-roadmap?role=${encodeURIComponent(name)}&product=${product}`);
      setPlans((p) => ({ ...p, [name]: r?.data || null }));
    } catch (_e) {
      setPlans((p) => ({ ...p, [name]: null }));
    } finally {
      setPlanBusy("");
    }
  };

  if (!cf || !roles.length) {
    return <div className="k-quiet" data-testid="career-fit-empty">Career fit builds once your read is in.</div>;
  }

  const QuadBadge = ({ q }) => {
    const map = {
      "sweet spot": ["178 82% 34%", "your sweet spot"],
      "stretch": ["219 76% 47%", "a stretch worth taking"],
      "backup": ["32 80% 45%", "a solid backup"],
      "not yet": ["258 60% 55%", "not yet, but reachable"],
    };
    const [c, label] = map[q] || map["stretch"];
    return <span className="k-chip !text-xs" style={{ color: `hsl(${c})`, background: `hsl(${c} / 0.12)`, border: "none" }}>{label}</span>;
  };

  return (
    <div data-testid="career-fit-panel">
      {/* Master recommendation */}
      <div className="k-accent-card k-acc-violet mb-6" data-testid="career-fit-master">
        <div className="flex items-center gap-2 mb-1">
          <Star size={15} style={{ color: "hsl(258 60% 55%)", fill: "hsl(258 60% 55%)" }} />
          <span className="k-acc-label">your strongest match</span>
        </div>
        <h2 className="klareet-serif mb-2" style={{ fontSize: "clamp(1.8rem,4vw,2.6rem)", lineHeight: 1.05, color: "hsl(var(--klareet-ink))" }} data-testid="career-master-name">
          {masterRole.name}
        </h2>
        <p className="text-sm mb-4" style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.55 }}>{masterRole.one_liner}</p>
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-3"><Ring pct={masterRole.fit} color={HUES.violet} size={72} /><div><div className="k-acc-label">career fit</div><div className="k-quiet !text-xs">how well it fits you</div></div></div>
          <div className="flex items-center gap-3"><Ring pct={masterRole.readiness} color={HUES.teal} size={72} /><div><div className="k-acc-label">readiness</div><div className="k-quiet !text-xs">how set up you are</div></div></div>
        </div>
      </div>

      {/* Role pills */}
      <p className="k-quiet !text-sm mb-2">Tap any path. Each one scores itself against who you are.</p>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="career-role-pills">
        {roles.map((r) => {
          const isSel = r.name === selected;
          return (
            <button key={r.name} onClick={() => setSelected(r.name)}
              className="k-pill !py-1.5 !px-3.5 !text-sm"
              style={isSel ? { background: "hsl(258 60% 55%)", color: "white", borderColor: "hsl(258 60% 55%)" } : {}}
              data-testid={`career-role-pill-${r.name}`}>
              {r.name === cf.master && <Star size={11} className="inline mr-1" style={{ marginBottom: 2 }} />}{r.name}
            </button>
          );
        })}
      </div>

      {/* Selected role detail */}
      {sel && (
        <div className="k-card mb-6" data-testid="career-role-detail">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <h3 className="klareet-serif" style={{ fontSize: "1.5rem", color: "hsl(var(--klareet-ink))" }} data-testid="career-detail-name">{sel.name}</h3>
              <div className="mt-1"><QuadBadge q={sel.quadrant} /></div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center"><Ring pct={sel.fit} color={HUES.violet} size={62} /><div className="k-acc-label mt-1">fit</div></div>
              <div className="text-center"><Ring pct={sel.readiness} color={HUES.teal} size={62} /><div className="k-acc-label mt-1">readiness</div></div>
            </div>
          </div>
          <p className="text-sm mb-4" style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.55 }}>{sel.one_liner}</p>
          <div className="space-y-3">
            <DetailRow icon={<ShieldCheck size={15} style={{ color: "hsl(178 82% 34%)" }} />} label="Why it fits you" text={sel.why} />
            <DetailRow icon={<TrendingUp size={15} style={{ color: "hsl(219 76% 47%)" }} />} label="What to build" text={sel.build} />
            <DetailRow icon={<Target size={15} style={{ color: "hsl(258 60% 55%)" }} />} label="For your goal" text={sel.connect} />
          </div>
          <RolePlan name={sel.name} plan={plans[sel.name]} busy={planBusy === sel.name} onLoad={() => loadPlan(sel.name)} />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <FitReadinessMap roles={roles} selected={selected} onSelect={setSelected} master={cf.master} />
        <RoleFitRanking roles={roles} selected={selected} onSelect={setSelected} />
      </div>
    </div>
  );
}

function DetailRow({ icon, label, text }) {
  return (
    <div className="flex gap-3">
      <span className="shrink-0 mt-0.5">{icon}</span>
      <div><div className="k-acc-label">{label}</div><div className="text-sm" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>{text}</div></div>
    </div>
  );
}

// ---- Inline twelve week plan for the selected role --------------------
function RolePlan({ name, plan, busy, onLoad }) {
  if (!plan) {
    return (
      <button
        onClick={onLoad}
        disabled={busy}
        className="k-btn k-btn-outline mt-5"
        data-testid="role-plan-open"
      >
        <CalendarRange size={14} /> {busy ? "Building your plan" : `See a 12 week plan for ${name}`}
      </button>
    );
  }
  return (
    <div className="mt-5 pt-4 border-t" style={{ borderColor: "hsl(var(--klareet-line-soft))" }} data-testid="role-plan">
      <div className="k-acc-label mb-1 flex items-center gap-2"><CalendarRange size={13} /> your 12 week plan</div>
      <p className="k-quiet !text-xs mb-4">{plan.connect}</p>
      <ol className="space-y-4 pl-6 relative" data-testid="role-plan-weeks">
        <span className="absolute left-[7px] top-1 bottom-1 w-px" style={{ background: "hsl(var(--klareet-line))" }} aria-hidden="true" />
        {(plan.weeks || []).map((w, i) => (
          <li key={i} className="relative" data-testid={`role-plan-week-${w.week}`}>
            <span className="absolute -left-6 top-0.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: "hsl(258 60% 55% / 0.15)" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "hsl(258 60% 55%)" }} />
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="klareet-mono !text-[11px]" style={{ color: "hsl(258 60% 55%)" }}>week {w.week}</span>
              <span className="text-sm font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>{w.theme}</span>
              {w.focus && (
                <span className="k-chip !text-[10px]" style={{ color: "hsl(178 82% 30%)", background: "hsl(178 82% 34% / 0.12)", border: "none" }}>{w.focus}</span>
              )}
            </div>
            <p className="text-sm mt-0.5" style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.5 }}>{w.action}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ---- Driving goal switcher --------------------------------------------
export function DrivingGoalBar({ base = "", onChange }) {
  const [goals, setGoals] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    client.get("/engagement/goal").then((r) => setGoals(r?.data?.items || [])).catch(() => setGoals([]));
  };
  useEffect(load, []);

  const driving = (goals || []).find((g) => g.is_driving) || (goals || [])[0] || null;

  const pick = async (id) => {
    if (busy) return;
    setBusy(true); setOpen(false);
    try {
      await client.post(`/engagement/goal/${id}/drive`);
      load();
      if (onChange) onChange();
    } catch (_e) { /* silent */ } finally { setBusy(false); }
  };

  if (goals === null) return null;
  if (!goals.length) {
    return (
      <div className="k-accent-card k-acc-blue mb-6 flex items-center gap-3 flex-wrap" data-testid="driving-goal-bar">
        <Target size={16} style={{ color: "hsl(219 76% 47%)" }} />
        <span className="text-sm" style={{ color: "hsl(var(--klareet-ink))" }}>Set a goal and the whole Snapshot re-tunes around it.</span>
        <a href={`${base}/home`} className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" data-testid="driving-goal-add">Add a goal <ArrowRight size={12} /></a>
      </div>
    );
  }

  return (
    <div className="k-accent-card k-acc-blue mb-6" data-testid="driving-goal-bar">
      <div className="flex items-center gap-3 flex-wrap">
        <Target size={16} style={{ color: "hsl(219 76% 47%)" }} />
        <span className="k-acc-label">driving goal</span>
        <div className="relative">
          <button onClick={() => setOpen((o) => !o)} disabled={goals.length < 2}
            className="flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: "hsl(var(--klareet-ink))" }} data-testid="driving-goal-current">
            {driving?.title || "Choose a goal"}
            {goals.length > 1 && <ChevronDown size={15} style={{ color: "hsl(var(--klareet-muted))" }} />}
          </button>
          {open && goals.length > 1 && (
            <div className="absolute z-30 mt-2 left-0 w-72 rounded-xl border shadow-lg p-1.5"
              style={{ background: "hsl(var(--card))", borderColor: "hsl(var(--klareet-line))" }} data-testid="driving-goal-menu">
              {goals.map((g) => (
                <button key={g.id} onClick={() => pick(g.id)}
                  className="w-full flex items-center gap-2 text-left px-2.5 py-2 rounded-lg text-sm hover:bg-[hsl(var(--klareet-canvas-2))]"
                  style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`driving-goal-option-${g.id}`}>
                  <ChevronRight size={13} style={{ color: g.is_driving ? "hsl(219 76% 47%)" : "transparent" }} />
                  {g.title}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="k-quiet !text-xs mt-2">Everything below, your read, your fit, your roadmap, is tuned to this.</p>
    </div>
  );
}
